import React from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Icon,
  Badge
} from '@chakra-ui/react'
import { FiArrowRight } from 'react-icons/fi'
import { SpotlightHeader } from '../components/Spotlight/SpotlightHeader'
import { SpotlightFooter } from '../components/Spotlight/SpotlightFooter'

export const FAQPage: React.FC = () => {
  const faqs = [
    {
      question: "What is Livewello Spotlight?",
      answer: "Spotlight is a client-acquisition platform for wellness practitioners. We generate Meta carousel ads (4-7 slides) from your answers and run them on Meta to bring potential clients directly to your Spotlight-only landing page. No marketing skills required."
    },
    {
      question: "How does it work?",
      answer: "Answer 4 questions and upload a photo (60-90 seconds), preview your carousel instantly, optionally refine story beats, then choose your CTA and select a visibility pack. We automatically run your carousel ads on Meta when you activate a pack. All traffic goes to your Spotlight landing page designed to convert."
    },
    {
      question: "Do I need marketing experience?",
      answer: "No. Spotlight is designed for practitioners who want to get clients without learning ads, funnels, or marketing. We handle all the technical aspects, carousel ad generation, campaign management, and optimization. You just focus on your practice."
    },
    {
      question: "What's included with my Spotlight page?",
      answer: "Your Spotlight page builder, carousel ad generation, and analytics dashboard are included. Pay only for impression-based visibility packs when you activate them. No subscriptions, no monthly fees."
    },
    {
      question: "How much do visibility packs cost?",
      answer: "Visibility packs range from $199 (10K impressions) to $1,199 (100K impressions). The more impressions you buy, the lower the cost per 1,000 impressions. You can see detailed pricing and expected results on our Pricing page."
    },
    {
      question: "What results can I expect?",
      answer: "Results vary based on your specialty, targeting, and landing page conversion rate. We provide conservative estimates: 10K impressions typically produce 40-80 views, 1-4 leads, and 0-1 clients. Higher impression packs generally yield proportionally better results. All traffic goes to your Spotlight landing page designed to convert clicks into leads."
    },
    {
      question: "Do I need a website?",
      answer: "No. Your Spotlight page serves as your professional landing page. It's optimized for conversions and mobile-friendly. You can share the link anywhere, embed it on existing sites, or use it as your primary web presence."
    },
    {
      question: "How long does it take to set up?",
      answer: "Answer 4 questions and upload a photo (60-90 seconds), then preview your carousel instantly. You can optionally refine story beats, choose your CTA, and select a visibility pack. Once you've purchased a pack, carousel ads can start running on Meta within 24-48 hours."
    },
    {
      question: "Can I change my visibility level?",
      answer: "Yes. You can purchase additional impression packs at any time, or start with a smaller pack and scale up as you see results. There are no long-term contracts or commitments."
    },
    {
      question: "What about the directory listing?",
      answer: "Your profile is automatically included in our public wellness directory as a bonus benefit. However, the primary value of Spotlight comes from the managed ad campaigns that drive targeted traffic to your page. The directory provides additional organic visibility but is not the core offering."
    },
    {
      question: "How do I track my results?",
      answer: "You'll have access to a simple analytics dashboard showing impressions, page views, clicks, and leads. We'll notify you when leads come in so you can respond quickly. No complex marketing metrics or confusing data—just the numbers that matter."
    },
    {
      question: "What if I'm not getting results?",
      answer: "We continuously optimize your campaigns for best performance. If you're not seeing expected results, we'll work with you to improve your page copy, adjust targeting, or refine ad creatives. Our goal is your success."
    }
  ]

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="white">
      <SpotlightHeader />
      
      <Box flex={1}>
        {/* Hero */}
        <Box bgGradient="linear(to-br, brand.50, blue.50)" py={{ base: 12, md: 20 }}>
          <Container maxW="6xl">
            <VStack gap={6} textAlign="center" maxW="3xl" mx="auto">
              <Badge colorPalette="brand" size="lg" px={4} py={2} borderRadius="full">
                FAQ
              </Badge>
              <Heading size={{ base: '3xl', md: '4xl' }} color="gray.900" lineHeight="shorter">
                Frequently Asked Questions
              </Heading>
              <Text fontSize="xl" color="gray.700" lineHeight="tall">
                Everything you need to know about Spotlight and getting more clients
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* FAQ Items */}
        <Container maxW="4xl" py={{ base: 12, md: 16 }}>
          <VStack gap={6} align="stretch">
            {faqs.map((faq, idx) => (
              <Card.Root
                key={idx}
                borderWidth="1px"
                borderColor="gray.200"
                bg="white"
                borderRadius="xl"
                shadow="sm"
              >
                <Card.Body p={6}>
                  <VStack align="start" gap={3}>
                    <Heading size="md" color="gray.900">
                      {faq.question}
                    </Heading>
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      {faq.answer}
                    </Text>
                  </VStack>
                </Card.Body>
              </Card.Root>
            ))}
          </VStack>
        </Container>

        {/* CTA */}
        <Box bg="brand.800" color="white" py={{ base: 16, md: 20 }}>
          <Container maxW="4xl">
            <VStack gap={6} textAlign="center">
              <Heading size={{ base: '2xl', md: '3xl' }}>
                Still have questions?
              </Heading>
              <Text fontSize="xl" color="whiteAlpha.900" maxW="2xl">
                Get in touch with our team or start building your Spotlight page to see how it works.
              </Text>
              <Button
                as={Link}
                to="/for-services/onboarding/basic-setup"
                size="xl"
                bg="white"
                color="brand.800"
                fontWeight="bold"
                px={8}
                py={6}
                _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
                transition="all 0.2s"
              >
                <HStack gap={2}>
                  <Text>Start Getting Clients</Text>
                  <Icon as={FiArrowRight} />
                </HStack>
              </Button>
            </VStack>
          </Container>
        </Box>
      </Box>

      {/* Mobile Sticky CTA */}
      <Box 
        position="fixed" 
        bottom={0} 
        left={0} 
        right={0} 
        bg="white" 
        p={4} 
        borderTopWidth="1px" 
        borderColor="gray.200"
        shadow="xl" 
        display={{ base: 'block', md: 'none' }}
        zIndex={1000}
      >
        <Button 
          as={Link}
          to="/for-services/onboarding/basic-setup"
          w="full" 
          colorPalette="brand" 
          size="lg" 
          fontWeight="bold"
        >
          <HStack gap={2}>
            <Text>Start Getting Clients</Text>
            <Icon as={FiArrowRight} />
          </HStack>
        </Button>
      </Box>

      <SpotlightFooter />
    </Box>
  )
}
